import { ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InMemoryCacheService, UtilsService } from '../common';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppMenuitemComponent } from './app.menuitem.component';

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, AppMenuitemComponent],
    templateUrl: './app.menu.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppMenuComponent implements OnInit, OnDestroy {

    model: any[] = [];
    private destroy$ = new Subject<void>();

    constructor(private cache: InMemoryCacheService, private utils: UtilsService, private router: Router, private cdr: ChangeDetectorRef) {}

    ngOnInit() {
        this.cache.mainMenu
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
            if (data) {
                this.utils.logger.info(`AppMenuComponent.ngOnInit() setting menu data`);
                this.model = data;
                this.updateActiveState();
                this.cdr.markForCheck();
            }
       });

       this.router.events
       .pipe(takeUntil(this.destroy$))
       .subscribe(() => {
            this.updateActiveState();
            this.cdr.markForCheck();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    updateActiveState() {
        const currentPath = this.router.url; // Get the current path
        //this.utils.logger.info(`AppMenuComponent.updateActiveState() - current path: ${currentPath}`);

        // Reset all active states
        this.resetActiveState(this.model);

        // Find and set the active menu item
        this.model.forEach(item => {
            if (this.setActiveState(item, currentPath)) {
                return; // Stop further checks once the active item is found
            }
        });
    }

    setActiveState(item: any, currentPath: string): boolean {
        const pathOnly = currentPath.split('?')[0];
        if (item.routerLink && pathOnly === item.routerLink.join('/')) {
            item.active = true; // Set the item as active if the route matches
            return true; // Return true to indicate a match
        }

        if (item.items) {
            for (const subItem of item.items) {
                if (this.setActiveState(subItem, currentPath)) {
                    item.active = true; // Set the parent as active if a child matches
                    return true; // Return true to indicate a match
                }
            }
        }

        return false; // No match found
    }

    onMenuClick(clickedItem: any) {
        this.resetActiveState(this.model);

        if (clickedItem.items) {
            clickedItem.active = !clickedItem.active;
        } else {
            clickedItem.active = true;
        }
    }

    resetActiveState(items: any[]) {
        items.forEach(item => {
            item.active = false;
            if (item.items) {
                this.resetActiveState(item.items);
            }
        });
    }

    private isMenuActive(routes: string[]): boolean {
        const currentPath = this.router.url; // Get the current path
        this.utils.logger.info(`AppMenuComponent.isMenuActive() - current path: ${currentPath}`);
        this.utils.logger.info(`AppMenuComponent.isMenuActive() - checking against routes: ${JSON.stringify(routes)}`);

        // Match route prefixes but avoid matching "/" for all routes
        return routes.some(route => route && route !== "/" && currentPath.startsWith(route));
    }

    generateId(productName: string): string {
        return productName.replace(/\s+/g, '-') + '-button';
    }

    trackByLabel(index: number, item: any): string {
        return item?.label || index;
    }
}
